import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import PageMenu from '../../blocks/PageMenu/PageMenu';
import Delete from "../../../img/icons/svg/botones/delete.png";
import down from "../../../img/icons/Delta/down.png";
import ICON_URL from "../../../img/icons/svg/botones/upload_icon.svg";
import ProgressBarBattery from 'components/ProgressBarBattery/ProgressBarBattery';
import useVerifyUserData from 'services/user/useAfiliationVerificationUser';
import './AffiliationVerificationForm.scss';
import '../../blocks/ManagementNewForms/Inputs/sccs/FormInputSubmit.scss';
import '../../blocks/ManagementNewForms/Inputs/sccs/FormInputArchivoReducido.scss';
import { clearAllCookies } from 'utils/cookieUtils';

export interface AffiliationFormData {
  acceptDeclaration: boolean;
}

interface SelectedFileEntry {
  files: File[];
  documentName: string;
}

export interface Base64FileEntry {
  fileName: string;
  fileType: string;
  base64: string;
}
interface IVerifyUserData {
  documentacionIPF: Documento[];
  documentacionContratoTrabajo: Documento[];
}

interface Documento {
  id: string | null;
  nombre: string;
  extension: string;
  base64: string;
}

export interface AffiliationBase64Payload {
  ipfImage: Base64FileEntry[];
  workContract: Base64FileEntry[];
  acceptDeclaration: boolean;
}

interface AffiliationVerificationFormProps { }

const ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'zip', '7z', 'txt',
  'xls', 'xlsx', 'csv', 'odt', 'ods',
  'jpg', 'jpeg', 'png', 'webp'
];

const PROHIBITED_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'sh', 'msi', 'vbs', 'scr', 'js', 'jar',
  'html', 'htm', 'mhtml', 'jfif', 'pptx'
];

const SUSPICIOUS_PATTERNS = [
  /<script>/i,
  /eval\(/i,
  /powershell/i,
  /cmd\.exe/i,
  /wscript/i,
  /cscript/i,
  /vbscript/i,
  /onerror=/i,
  /iframe/i,
  /document\.write/i,
  /base64,/i,
  /data:/i,
];

const MAX_SIZE_MB = 40;

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const AffiliationVerificationForm: React.FC<AffiliationVerificationFormProps> = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch
  } = useForm<AffiliationFormData>();

  const acceptDeclaration = watch('acceptDeclaration');

  const { VerifyUserData, errorVerifyUserData, loadingVerifyUserData } = useVerifyUserData();
  const { instance } = useMsal();

  const handleCloseAndLogout = async () => {
    // Limpiar cookies
    clearAllCookies({ secure: true });
    // Limpiar sessionStorage y localStorage
    sessionStorage.clear();
    localStorage.clear();
    // Cerrar sesión MSAL
    try {
      await instance.handleRedirectPromise();
    } catch (_) { }
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: '/inicio' });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: SelectedFileEntry }>({});
  const [fileErrors, setFileErrors] = useState<{ [key: string]: string }>({});
  const [totalSize, setTotalSize] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const ipfImageRef = useRef<HTMLInputElement>(null);
  const workContractRef = useRef<HTMLInputElement>(null);

  const hasSelectedFiles = Object.keys(selectedFiles).length > 0;

  useEffect(() => {
    if (!hasSelectedFiles) setShowProgressBar(false);
  }, [hasSelectedFiles]);

  const checkForHiddenScripts = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        resolve(SUSPICIOUS_PATTERNS.some(pattern => pattern.test(content)));
      };
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    documentId: string,
    documentName: string
  ) => {
    const allFiles = Array.from(e.target.files as FileList);
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    const invalidExtensions: string[] = [];

    for (const file of allFiles) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!ALLOWED_EXTENSIONS.includes(fileExtension || '') || PROHIBITED_EXTENSIONS.includes(fileExtension || '')) {
        invalidFiles.push(file);
        if (fileExtension && !invalidExtensions.includes(fileExtension)) {
          invalidExtensions.push(fileExtension);
        }
        continue;
      }

      if (fileExtension === 'zip' || fileExtension === '7z') {
        validFiles.push(file);
      } else {
        const hasHiddenScript = await checkForHiddenScripts(file);
        if (hasHiddenScript) {
          invalidFiles.push(file);
          if (fileExtension && !invalidExtensions.includes(fileExtension)) {
            invalidExtensions.push(fileExtension);
          }
        } else {
          validFiles.push(file);
        }
      }
    }

    if (invalidFiles.length > 0) {
      const extensionsText = invalidExtensions.length > 1
        ? `extensiones ${invalidExtensions.join(', ')}`
        : `extensión ${invalidExtensions[0]}`;
      setFileErrors(prev => ({ ...prev, [documentId]: `Algunos archivos con ${extensionsText} no están permitidos.` }));
      e.target.value = '';
      return;
    }

    setFileErrors(prev => ({ ...prev, [documentId]: '' }));

    if (validFiles.length > 0) {
      setShowProgressBar(true);
      setSelectedFiles(prev => {
        const existingFiles = prev[documentId]?.files || [];
        const updatedFiles = [...existingFiles, ...validFiles];
        const updated = { ...prev, [documentId]: { files: updatedFiles, documentName } };
        const newTotalSizeMB = Object.values(updated).reduce((acc, item) =>
          acc + item.files.reduce((sum, file) => sum + file.size, 0), 0) / (1024 * 1024);
        setTotalSize(parseFloat(newTotalSizeMB.toFixed(1)));
        return updated;
      });
    }
  };

  const removeFile = (documentId: string, fileIndex: number) => {
    setSelectedFiles(prev => {
      const updatedFiles = prev[documentId]?.files.filter((_, i) => i !== fileIndex) || [];
      let updated: typeof prev;
      if (updatedFiles.length === 0) {
        const { [documentId]: _, ...rest } = prev;
        updated = rest;
      } else {
        updated = { ...prev, [documentId]: { ...prev[documentId], files: updatedFiles } };
      }
      const newTotalSizeMB = Object.values(updated).reduce((acc, item) =>
        acc + item.files.reduce((sum, file) => sum + file.size, 0), 0) / (1024 * 1024);
      setTotalSize(parseFloat(newTotalSizeMB.toFixed(1)));
      return updated;
    });
  };

  const onSubmit = async () => {
    let hasErrors = false;
    if (!selectedFiles['ipfImage'] || selectedFiles['ipfImage'].files.length === 0) {
      setFileErrors(prev => ({ ...prev, ipfImage: 'La imagen de IPF es obligatoria.' }));
      hasErrors = true;
    }
    if (!selectedFiles['workContract'] || selectedFiles['workContract'].files.length === 0) {
      setFileErrors(prev => ({ ...prev, workContract: 'El contrato de trabajo es obligatorio.' }));
      hasErrors = true;
    }
    if (hasErrors) return;

    try {
      const toDocumento = async (file: File): Promise<Documento> => {
        const nameParts = file.name.split('.');
        const extension = nameParts.pop() ?? '';
        const nombre = nameParts.join('.');
        return { id: null, nombre, extension, base64: await toBase64(file) };
      };

      const toDocumentos = (files: File[]) => Promise.all(files.map(toDocumento));

      const payload: IVerifyUserData = {
        documentacionIPF: await toDocumentos(selectedFiles['ipfImage'].files),
        documentacionContratoTrabajo: await toDocumentos(selectedFiles['workContract'].files),
      };

      const result = await VerifyUserData(payload);
      if (result === false) {
        setShowErrorModal(true);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      setShowErrorModal(true);
    }
  };

  return (
    <>
      <PageMenu title="Verificación de Afiliación a la mutua" subtitle="Verifica tus documentos"/>
      <div className="affiliation-verification">

        <div className="affiliation-verification__instructions">
          <p>
            Si desea que alguna persona del área de afiliación se ponga en contacto con usted, complete la Documentación que le
            solicitamos y seleccione la <strong>ENVIAR DOCUMENTACIÓN</strong>
          </p>
          {/*   <ul className="affiliation-verification__requirements">
          <li>IPF - {'<Valor de IPF>'}</li>
          <li>Correo electrónico - {'<Valor de correo electrónico>'}</li>
          <li>Teléfono móvil- {'<Valor de teléfono móvil>'}</li>
        </ul> */}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="affiliation-form">
          <div className="accordion accordion-flush" id="accordionDocumentacion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="heading-documentacion">
                <button
                  type="button"
                  className="accordion-button header-acordeon-title"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapse-documentacion"
                  aria-expanded="false"
                  aria-controls="collapse-documentacion"
                >
                  <h1 className="ml-4 title-gestion title-gestion--sm">Documentación</h1>
                </button>
              </h2>
              <div
                id="collapse-documentacion"
                className="accordion-collapse collapse show"
                aria-labelledby="heading-documentacion"
              >
                <div className="accordion-body affiliation-form__section-content">
                  {/* Campo: Imagen de IPF */}
                  <div className="affiliation-form__field">
                    <label className="affiliation-form__label">
                      Imagen de IPF (ambas caras para DNI y NIE).
                    </label>
                    <div
                      onClick={() => ipfImageRef.current?.click()}
                      className="container btn-upload d-flex align-items-center justify-content-center"
                    >
                      <span className="mx-auto">
                        {(selectedFiles['ipfImage']?.files.length ?? 0) > 0
                          ? `Archivos adjuntos: ${selectedFiles['ipfImage'].files.length}`
                          : 'Adjunte un archivo'}
                      </span>
                      <img src={ICON_URL} alt="Subir" className="icon-upload" />
                    </div>
                    <input
                      type="file"
                      id="ipfImage"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileChange(e, 'ipfImage', 'Imagen de IPF')}
                      ref={ipfImageRef}
                      hidden
                    />
                    {fileErrors['ipfImage'] && <p className="error m-0">{fileErrors['ipfImage']}</p>}
                    {(selectedFiles['ipfImage']?.files.length ?? 0) > 0 && (
                      <details className="mt-2 container">
                        <summary className="list-files w-100 text-start">
                          <span className="p-2">Archivos adjuntados</span>
                          <img src={down} alt="" aria-hidden="true" className="iconDesplegable" />
                        </summary>
                        <ul className="list-group">
                          {selectedFiles['ipfImage'].files.map((file, idx) => {
                            const sizeText = file.size < 1024 * 1024
                              ? `${(file.size / 1024).toFixed(2)} KB`
                              : `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
                            return (
                              <li
                                key={`${file.name}-${file.size}`}
                                className="list-group-item d-flex justify-content-between align-items-center file-item"
                              >
                                <div className="file-info">
                                  <span className="file-name">
                                    <i className="bi bi-file-earmark me-2" />
                                    {file.name}
                                  </span>
                                  <div className="file-size-dropdown">Peso del Archivo: {sizeText}</div>
                                </div>
                                <button
                                  type="button"
                                  className="remove-file-btn me-2"
                                  aria-label={`Eliminar archivo ${file.name}`}
                                  onClick={() => removeFile('ipfImage', idx)}
                                >
                                  <img src={Delete} alt="" aria-hidden="true" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    )}
                  </div>

                  {/* Campo: Contrato de trabajo */}
                  <div className="affiliation-form__field">
                    <label className="affiliation-form__label">
                      Contrato de trabajo en vigor o último contrato de trabajo
                    </label>
                    <div
                      onClick={() => workContractRef.current?.click()}
                      className="container btn-upload d-flex align-items-center justify-content-center"
                    >
                      <span className="mx-auto">
                        {(selectedFiles['workContract']?.files.length ?? 0) > 0
                          ? `Archivos adjuntos: ${selectedFiles['workContract'].files.length}`
                          : 'Adjunte un archivo'}
                      </span>
                      <img src={ICON_URL} alt="Subir" className="icon-upload" />
                    </div>
                    <input
                      type="file"
                      id="workContract"
                      multiple
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'workContract', 'Contrato de trabajo')}
                      ref={workContractRef}
                      hidden
                    />
                    {fileErrors['workContract'] && <p className="error m-0">{fileErrors['workContract']}</p>}
                    {(selectedFiles['workContract']?.files.length ?? 0) > 0 && (
                      <details className="mt-2 container">
                        <summary className="list-files w-100 text-start">
                          <span className="p-2">Archivos adjuntados</span>
                          <img src={down} alt="" aria-hidden="true" className="iconDesplegable" />
                        </summary>
                        <ul className="list-group">
                          {selectedFiles['workContract'].files.map((file, idx) => {
                            const sizeText = file.size < 1024 * 1024
                              ? `${(file.size / 1024).toFixed(2)} KB`
                              : `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
                            return (
                              <li
                                key={`${file.name}-${file.size}`}
                                className="list-group-item d-flex justify-content-between align-items-center file-item "
                              >
                                <div className="file-info">
                                  <span className="file-name">
                                    <i className="bi bi-file-earmark me-2" />
                                    {file.name}
                                  </span>
                                  <div className="file-size-dropdown">Peso del Archivo: {sizeText}</div>
                                </div>
                                <button
                                  type="button"
                                  className="remove-file-btn me-2"
                                  aria-label={`Eliminar archivo ${file.name}`}
                                  onClick={() => removeFile('workContract', idx)}
                                >
                                  <img src={Delete} alt="" aria-hidden="true" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Checkbox: Acepto la información */}
          <div className="affiliation-form__checkbox-field">
            <label className="affiliation-form__checkbox-label" htmlFor="acceptDeclaration">
              Acepto la información declarada
            </label>
            <label className={`accept-divider-wrapper ${isSubmitting ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                id="acceptDeclaration"
                className="accept-divider-checkbox mb-2"
                disabled={isSubmitting}
                {...register('acceptDeclaration', {
                  required: 'Debe aceptar la declaración para continuar'
                })}
              />
              <span className="accept-divider-line accept-divider-line--left mb-2"></span>
              <span className="accept-divider-line accept-divider-line--right mb-2"></span>
            </label>
            {errors.acceptDeclaration && (
              <span className="affiliation-form__error affiliation-form__error--checkbox">
                {errors.acceptDeclaration.message}
              </span>
            )}
          </div>

          <ProgressBarBattery totalSize={totalSize} maxSize={MAX_SIZE_MB} showProgressBar={showProgressBar} />

          {/* Botón de envío */}
          <div className="affiliation-form__submit-wrapper">
            <button
              type="submit"
              className="btn-box btn-accent"
              disabled={isSubmitting || loadingVerifyUserData || !acceptDeclaration || !hasSelectedFiles || totalSize > MAX_SIZE_MB}
            >
              {loadingVerifyUserData ? 'ENVIANDO...' : 'ENVIAR DOCUMENTACIÓN'}
            </button>
          </div>

          {/* Texto legal */}
          <div className="affiliation-form__legal">
            <p>
              Esta solicitud va a ser tramitada por medios informáticos. Los datos personales que figuran en ella serán incorporados a un fichero de base de datos para el cálculo y control de la prestación económica que se le reconozca, y permanecerán bajo custodia de esta Entidad. En cualquier momento puede ejercitar su derecho de acceso, rectificación, cancelación y oposición sobre los datos incorporados al fichero ( art. 5 de la ley 15/1999 de 13 diciembre de Protección de datos de carácter personal, BOE del día 14).
            </p>
            <p>En cumplimiento de lo establecido en la normativa sobre de Protección de Datos de Carácter Personal, Solimat Mutua Colaboradora con la Seguridad Social nº 72 le informa que sus datos de carácter personal serán objeto de tratamiento, con la finalidad del mantenimiento y cumplimiento de la relación. La base jurídica que legitima el tratamiento de sus datos es el cumplimiento de una obligación legal. Sus datos serán conservados de forma indefinida en nuestra entidad para atender a las posibles responsabilidades derivadas del tratamiento. Usted podrá revocar el consentimiento prestado, así como ejercitar sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento, o portabilidad, en su caso, remitiendo un escrito a la siguiente dirección: Solimat, Mutua Colaboradora con la Seguridad Social nº 72, C/ Berna, 1, 45003, Toledo, adjuntando su NIF. Si considera que sus derechos no han sido debidamente atendidos, puede contactar con nuestro delegado de protección de datos lopd@solimat.com o interponer una reclamación frente a la Agencia Española de Protección de Datos.</p>
          </div>
        </form>
      </div>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered dialogClassName="modal-affiliation">
        <Modal.Header className="header-modal-observaciones-rd">
          <Modal.Title>
            <h2 className="title-obsevaciones-rd modal-affiliation__title">Error al enviar la documentación</h2>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="body-modal-observaciones-rd">
          <p className="text-body-modal-observaciones-rd">
            Se ha producido un error al enviar la documentación. Por favor, inténtelo de nuevo.
            Si el problema persiste, contacte con nosotros.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-affiliation__footer">
          <Button variant="secondary" onClick={handleCloseAndLogout}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered dialogClassName="modal-affiliation">
        <Modal.Header closeButton={false} className="header-modal-observaciones-rd">
          <Modal.Title>
            <h2 className="title-obsevaciones-rd modal-affiliation__title">Verificación de Afiliación a la mutua</h2>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="body-modal-observaciones-rd">
          <p className="text-body-modal-observaciones-rd">
            Hemos recibido correctamente su documentación y procederemos a verificar la información enviada.
            En breve recibirá un <strong>correo electrónico</strong> y una <strong>notificación</strong> con la resolución de su solicitud de afiliación.
            Gracias por confiar en SOLIMAT.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-affiliation__footer">
          <Button variant="secondary" onClick={handleCloseAndLogout}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AffiliationVerificationForm;
